import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import EntityForm from '../components/EntityForm/EntityForm';
import formStyles from '../components/EntityForm/EntityForm.module.css';
import pageStyles from './UserFormPage.module.css';
import HistoryAccordion from '../components/HistoryAccordion/HistoryAccordion';
import {
  getUser,
  createUser,
  updateUser,
  archiveUser,
  changeUserPassword,
  isAdmin as checkAdmin,
} from '../api';
import useEntityForm from '../hooks/useEntityForm';

const ROLES = ['Administrator', 'Supervisor', 'Staff'];

export default function UserFormPage({ user }) {
  const { id } = useParams();
  const isAdmin = checkAdmin(user);

  // Main form state
  const [username, setUsername] = useState('');
  const [role, setRole] = useState('');
  const [password, setPassword] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Change password state (edit mode only)
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [pwSubmitting, setPwSubmitting] = useState(false);
  const [pwSuccess, setPwSuccess] = useState('');
  const [pwError, setPwError] = useState('');

  const {
    isEdit,
    isBusy,
    formError,
    setFormError,
    setIsLoading,
    isArchived,
    setIsArchived,
    pageTitle,
    setPageTitle,
    handleCancel,
    handleArchive,
    handleRestore,
    runSubmit,
  } = useEntityForm({
    id,
    backPath: '/users',
    archiveFn: archiveUser,
    unarchiveFn: (entityId) => updateUser(entityId, { is_archived: false }),
    newLabel: 'New user',
  });

  useEffect(() => {
    if (!isEdit) return;
    setIsLoading(true);
    getUser(id)
      .then((data) => {
        const u = data.user;
        setUsername(u.username || '');
        setRole(u.role || '');
        setIsArchived(u.is_archived || false);
        setPageTitle(u.username || 'User');
      })
      .catch((err) => {
        setFormError(err.message || 'Failed to load user.');
        setPageTitle('User');
      })
      .finally(() => setIsLoading(false));
  }, [id, isEdit]);

  const validate = useCallback(() => {
    const errors = {};
    if (!username.trim()) errors.username = 'Username is required.';
    if (!role) errors.role = 'Please select a role.';
    if (!isEdit && !password) errors.password = 'Password is required.';
    return errors;
  }, [username, role, password, isEdit]);

  const handleSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setFormError('');
      const errors = validate();
      if (Object.keys(errors).length > 0) {
        setFieldErrors(errors);
        return;
      }
      setFieldErrors({});
      await runSubmit(() =>
        isEdit
          ? updateUser(id, { username: username.trim(), role })
          : createUser({ username: username.trim(), password, role })
      );
    },
    [id, isEdit, username, role, password, validate, runSubmit, setFormError]
  );

  const isSelf = user && String(user.id) === String(id);

  const handlePasswordChange = useCallback(async () => {
    setPwError('');
    setPwSuccess('');
    if (isSelf && !currentPassword) { setPwError('Current password is required.'); return; }
    if (!newPassword) { setPwError('New password is required.'); return; }
    setPwSubmitting(true);
    try {
      const payload = { new_password: newPassword };
      if (isSelf) payload.current_password = currentPassword;
      await changeUserPassword(id, payload);
      setPwSuccess('Password updated successfully.');
      setCurrentPassword('');
      setNewPassword('');
    } catch (err) {
      setPwError(err.message || 'Failed to update password.');
    } finally {
      setPwSubmitting(false);
    }
  }, [id, isSelf, currentPassword, newPassword]);

  return (
    <EntityForm
      title={pageTitle}
      onCancel={handleCancel}
      onSubmit={handleSubmit}
      submitLabel={isEdit ? 'Save changes' : 'Create user'}
      isSubmitting={isBusy}
      error={formError}
      canDelete={isEdit && isAdmin && !isArchived}
      onDelete={handleArchive}
      confirmMessage={`Archive "${username}"? This will remove them from active use.`}
      canRestore={isEdit && isAdmin && isArchived}
      onRestore={() => handleRestore(`Restore "${username}"? They will become active again.`)}
      restoreLabel="Restore user"
      belowCard={isEdit ? <HistoryAccordion entityPath={`/users/${id}`} /> : null}
    >
      {/* Username — full width */}
      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="userUsername">
          Username <span className={formStyles.required} aria-hidden="true">*</span>
        </label>
        <input
          id="userUsername"
          type="text"
          className={`${formStyles.input} ${fieldErrors.username ? formStyles.inputError : ''}`}
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            if (fieldErrors.username) setFieldErrors((prev) => ({ ...prev, username: '' }));
          }}
          placeholder="e.g. jsmith"
          disabled={isBusy}
          autoFocus={!isEdit}
          required
        />
        {fieldErrors.username && (
          <span className={formStyles.fieldError} role="alert">{fieldErrors.username}</span>
        )}
      </div>

      {/* Role — full width */}
      <div className={formStyles.field}>
        <label className={formStyles.label} htmlFor="userRole">
          Role <span className={formStyles.required} aria-hidden="true">*</span>
        </label>
        <select
          id="userRole"
          className={`${formStyles.select} ${fieldErrors.role ? formStyles.inputError : ''}`}
          value={role}
          onChange={(e) => {
            setRole(e.target.value);
            if (fieldErrors.role) setFieldErrors((prev) => ({ ...prev, role: '' }));
          }}
          disabled={isBusy}
          required
        >
          <option value="">Select role…</option>
          {ROLES.map((r) => (
            <option key={r} value={r}>{r}</option>
          ))}
        </select>
        {fieldErrors.role && (
          <span className={formStyles.fieldError} role="alert">{fieldErrors.role}</span>
        )}
      </div>

      {/* Password — create mode only */}
      {!isEdit && (
        <div className={formStyles.field}>
          <label className={formStyles.label} htmlFor="userPassword">
            Password <span className={formStyles.required} aria-hidden="true">*</span>
          </label>
          <input
            id="userPassword"
            type="password"
            className={`${formStyles.input} ${fieldErrors.password ? formStyles.inputError : ''}`}
            value={password}
            onChange={(e) => {
              setPassword(e.target.value);
              if (fieldErrors.password) setFieldErrors((prev) => ({ ...prev, password: '' }));
            }}
            placeholder="Enter password"
            disabled={isBusy}
            required
          />
          {fieldErrors.password && (
            <span className={formStyles.fieldError} role="alert">{fieldErrors.password}</span>
          )}
        </div>
      )}

      {/* Change password section — edit mode only */}
      {isEdit && (
        <div className={pageStyles.passwordSection}>
          <p className={pageStyles.passwordHeading}>Change password</p>

          {isSelf && (
            <div className={formStyles.field}>
              <label className={formStyles.label} htmlFor="currentPassword">
                Current password
              </label>
              <input
                id="currentPassword"
                type="password"
                className={formStyles.input}
                value={currentPassword}
                onChange={(e) => { setCurrentPassword(e.target.value); setPwError(''); setPwSuccess(''); }}
                placeholder="Enter current password"
                disabled={pwSubmitting}
                autoComplete="current-password"
              />
            </div>
          )}

          <div className={formStyles.field}>
            <label className={formStyles.label} htmlFor="newPassword">
              New password
            </label>
            <input
              id="newPassword"
              type="password"
              className={formStyles.input}
              value={newPassword}
              onChange={(e) => { setNewPassword(e.target.value); setPwError(''); setPwSuccess(''); }}
              placeholder="Enter new password"
              disabled={pwSubmitting}
              autoComplete="new-password"
            />
          </div>

          <button
            type="button"
            className={pageStyles.passwordBtn}
            onClick={handlePasswordChange}
            disabled={pwSubmitting}
          >
            {pwSubmitting ? 'Updating…' : 'Update password'}
          </button>

          {pwSuccess && (
            <p className={pageStyles.passwordSuccess} role="status">{pwSuccess}</p>
          )}
          {pwError && (
            <p className={pageStyles.passwordError} role="alert">{pwError}</p>
          )}
        </div>
      )}
    </EntityForm>
  );
}
